"use client";

import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import { BUILDER_BASE_PRICE, BUILDER_STEPS } from "@/lib/constants";
import { trackEvent } from "@/lib/track";

type StepKey = (typeof BUILDER_STEPS)[number]["key"];

interface BuilderSelections {
  hair_type: string | null;
  base_type: string | null;
  texture: string | null;
  length: string | null;
  color: string | null;
  cap_size: string | null;
  extras: string | null;
}

interface PriceBreakdown {
  hair_type: number;
  base_type: number;
  texture: number;
  length: number;
  color: number;
  cap_size: number;
  extras: number;
}

interface BuilderState {
  currentStep: number;
  selections: BuilderSelections;
  priceBreakdown: PriceBreakdown;
  totalPrice: number;
  specialNotes: string;
}

type BuilderAction =
  | { type: "SET_OPTION"; key: StepKey; value: string; priceAdd: number }
  | { type: "NEXT_STEP" }
  | { type: "PREV_STEP" }
  | { type: "GO_TO_STEP"; step: number }
  | { type: "RESET" }
  | { type: "SET_NOTES"; notes: string }
  | { type: "RESTORE"; state: BuilderState };

const initialState: BuilderState = {
  currentStep: 0,
  selections: {
    hair_type: null,
    base_type: null,
    texture: null,
    length: null,
    color: null,
    cap_size: null,
    extras: null,
  },
  priceBreakdown: {
    hair_type: 0,
    base_type: 0,
    texture: 0,
    length: 0,
    color: 0,
    cap_size: 0,
    extras: 0,
  },
  totalPrice: BUILDER_BASE_PRICE,
  specialNotes: "",
};

function calculateTotal(breakdown: PriceBreakdown): number {
  return (
    BUILDER_BASE_PRICE +
    Object.values(breakdown).reduce((sum, val) => sum + val, 0)
  );
}

function builderReducer(state: BuilderState, action: BuilderAction): BuilderState {
  switch (action.type) {
    case "SET_OPTION": {
      // Find the index of the step being changed
      const changedStepIndex = BUILDER_STEPS.findIndex((s) => s.key === action.key);

      // Clear all selections & prices for steps AFTER the changed step
      const newSelections = { ...state.selections };
      const newBreakdown = { ...state.priceBreakdown };

      // Set the current step's value
      newSelections[action.key] = action.value;
      newBreakdown[action.key] = action.priceAdd;

      // Clear downstream steps
      for (let i = changedStepIndex + 1; i < BUILDER_STEPS.length; i++) {
        const laterKey = BUILDER_STEPS[i].key as StepKey;
        newSelections[laterKey] = null;
        newBreakdown[laterKey] = 0;
      }

      return {
        ...state,
        selections: newSelections,
        priceBreakdown: newBreakdown,
        totalPrice: calculateTotal(newBreakdown),
      };
    }
    case "NEXT_STEP":
      return {
        ...state,
        currentStep: Math.min(state.currentStep + 1, BUILDER_STEPS.length - 1),
      };
    case "PREV_STEP":
      return {
        ...state,
        currentStep: Math.max(state.currentStep - 1, 0),
      };
    case "GO_TO_STEP":
      return {
        ...state,
        currentStep: Math.max(0, Math.min(action.step, BUILDER_STEPS.length - 1)),
      };
    case "SET_NOTES":
      return {
        ...state,
        specialNotes: action.notes,
      };
    case "RESET":
      if (typeof window !== "undefined") {
        localStorage.removeItem("rw-builder-state");
      }
      return initialState;
    case "RESTORE":
      // Recompute totalPrice from the persisted breakdown — the stored totalPrice
      // may be stale if pricing logic changes between sessions.
      return {
        ...action.state,
        totalPrice: calculateTotal(action.state.priceBreakdown),
      };
    default:
      return state;
  }
}

const STORAGE_KEY = "rw-builder-state";

interface BuilderContextType {
  currentStep: number;
  selections: BuilderSelections;
  priceBreakdown: PriceBreakdown;
  totalPrice: number;
  specialNotes: string;
  setOption: (key: StepKey, value: string, priceAdd: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  setNotes: (notes: string) => void;
  reset: () => void;
  canProceed: boolean;
  isLastStep: boolean;
  isFirstStep: boolean;
  hasSavedState: boolean;
}

const BuilderContext = createContext<BuilderContextType | undefined>(undefined);

export function BuilderProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(builderReducer, initialState);

  // Restore saved state on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as BuilderState;
        if (parsed.selections && parsed.priceBreakdown) {
          dispatch({ type: "RESTORE", state: parsed });
        }
      }
    } catch {
      // ignore corrupted storage
    }
  }, []);

  // Persist to localStorage on state changes (skip initial state)
  useEffect(() => {
    const hasAnySelection = Object.values(state.selections).some((v) => v !== null);
    if (hasAnySelection) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } catch {
        // ignore storage full
      }
    }
  }, [state]);

  const hasSavedState = Object.values(state.selections).some((v) => v !== null);

  const setOption = useCallback(
    (key: StepKey, value: string, priceAdd: number) => {
      trackEvent("builder_option_selected", {
        step: state.currentStep,
        stepName: key,
        optionName: value,
        priceAddon: priceAdd,
      });
      dispatch({ type: "SET_OPTION", key, value, priceAdd });
    },
    [state.currentStep]
  );

  const nextStep = useCallback(() => dispatch({ type: "NEXT_STEP" }), []);
  const prevStep = useCallback(() => dispatch({ type: "PREV_STEP" }), []);
  const goToStep = useCallback(
    (step: number) => dispatch({ type: "GO_TO_STEP", step }),
    []
  );
  const setNotes = useCallback(
    (notes: string) => dispatch({ type: "SET_NOTES", notes }),
    []
  );
  const reset = useCallback(() => dispatch({ type: "RESET" }), []);

  const currentStepKey = BUILDER_STEPS[state.currentStep].key;
  const canProceed =
    currentStepKey === "extras" || state.selections[currentStepKey] !== null;
  const isLastStep = state.currentStep === BUILDER_STEPS.length - 1;
  const isFirstStep = state.currentStep === 0;

  return (
    <BuilderContext.Provider
      value={{
        currentStep: state.currentStep,
        selections: state.selections,
        priceBreakdown: state.priceBreakdown,
        totalPrice: state.totalPrice,
        specialNotes: state.specialNotes,
        setOption,
        nextStep,
        prevStep,
        goToStep,
        setNotes,
        reset,
        canProceed,
        isLastStep,
        isFirstStep,
        hasSavedState,
      }}
    >
      {children}
    </BuilderContext.Provider>
  );
}

export function useBuilder() {
  const context = useContext(BuilderContext);
  if (!context) {
    throw new Error("useBuilder must be used within BuilderProvider");
  }
  return context;
}
